import React, { memo, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ResultAutoSlideBox.scss';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';

interface ResultAutoSlideBoxProps {
  children: React.ReactNode;
  className?: string;
  step: number;
  onNextStep: () => void;
  onPrevStep: () => void;
  onPlayAgain?: () => void;
  canGoBack?: boolean;
  canPlayAgain?: boolean;
  animatedTime?: number;
  disableAnimation?: boolean;
}

export const ResultAutoSlideBox: React.FC<ResultAutoSlideBoxProps> = memo(
  ({
    children,
    className,
    step,
    canGoBack = true,
    onNextStep,
    onPrevStep,
    onPlayAgain = () => void 0,
    canPlayAgain = false,
    animatedTime = 5000, // Default 5 seconds
    disableAnimation = false,
  }) => {
    const [timeLeft, setTimeLeft] = useState(animatedTime / 1000);
    const hasCalledNextStep = useRef(false);

    useEffect(() => {
      if (disableAnimation) {
        return;
      }

      // Reset and start timer when step changes
      setTimeLeft(animatedTime / 1000);
      hasCalledNextStep.current = false;

      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            if (!hasCalledNextStep.current) {
              hasCalledNextStep.current = true;
              onNextStep();
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearInterval(timer);
      };
    }, [step, animatedTime, disableAnimation, onNextStep]);

    return (
      <div
        className={`result-auto-slide-box ${className || ''}`}
        data-testid="result-auto-slide-box"
      >
        {!disableAnimation && (
          <div
            className={`result-auto-slide-box__header ${
              canGoBack ? 'result-auto-slide-box__header--with-back' : ''
            }`}
          >
            {canGoBack && (
              <button
                onClick={onPrevStep}
                className="result-auto-slide-box__back-button"
                data-testid="back-button"
              >
                <ArrowBackIos className="result-auto-slide-box__back-button__icon" />
              </button>
            )}
            {canPlayAgain ? (
              <button onClick={onPlayAgain} className="result-auto-slide-box__play-again-button">
                <span className="result-auto-slide-box__play-again-text">Play again</span>
              </button>
            ) : (
              <div className="result-auto-slide-box__buttons-container">
                <div className="result-auto-slide-box__timer-container">
                  <span className="result-auto-slide-box__timer-container__timer">{timeLeft}s</span>
                </div>
                <button onClick={onNextStep} className="result-auto-slide-box__next-button">
                  <ArrowForwardIos className="result-auto-slide-box__next-button__icon" />
                </button>
              </div>
            )}
          </div>
        )}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            className="result-auto-slide-box__content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }
);

export default ResultAutoSlideBox;
